import { z } from 'zod';

/** Password rules: length beats complexity. Min 10, max 128 (PBKDF2 input).
 * Messages are user-facing — the API forwards them as `details.fieldErrors`. */
export const PasswordSchema = z
  .string()
  .min(10, 'Use at least 10 characters.')
  .max(128, 'That password is too long (128 characters max).');

/** Login only accepts an existing password, so it isn't held to the signup
 * policy — any non-empty string is checked against the stored hash, and a
 * length/content mismatch just comes back as "wrong email or password". */
export const LoginPasswordSchema = z.string().min(1, 'Enter your password.').max(128);

const EmailSchema = z
  .email('Enter a valid email address.')
  .max(200)
  .transform((s) => s.toLowerCase().trim());

const TurnstileSchema = z.string().min(1).max(4096);

/* POST /api/auth/request-otp — send a 6-digit code to an email. */
export const RequestOtpSchema = z.object({
  email: EmailSchema,
  /** Cloudflare Turnstile token from the widget. */
  turnstileToken: TurnstileSchema,
  /** "signup" gates the 18+ copy; "reset" skips the signups-open gate; "login"
   * is the "email me a code instead" fallback. */
  intent: z.enum(['signup', 'login', 'reset']),
});
export type RequestOtp = z.infer<typeof RequestOtpSchema>;

export const RequestOtpResponseSchema = z.object({
  ok: z.literal(true),
  /** Seconds until another code may be requested. */
  resendAfter: z.number().int().positive(),
  /** Dev only: the code, so local testing needs no inbox. Never set in prod. */
  devCode: z.string().optional(),
});
export type RequestOtpResponse = z.infer<typeof RequestOtpResponseSchema>;

/**
 * POST /api/auth/verify-otp — exchange a code for a session.
 * - no account for this email → `password` is **required** (signup).
 * - account exists + `password` → reset it (kills other sessions).
 * - account exists + no `password` → "email me a code" login.
 */
export const VerifyOtpSchema = z.object({
  email: EmailSchema,
  code: z.string().regex(/^\d{6}$/u, 'Enter the 6-digit code'),
  password: PasswordSchema.optional(),
  /** What the client thinks it's doing — lets the server reject the mismatched
   * case (e.g. "signup" for an email that already has an account). */
  purpose: z.enum(['signup', 'reset', 'login']).default('login'),
});
export type VerifyOtp = z.infer<typeof VerifyOtpSchema>;

/* POST /api/auth/login — everyday email + password sign-in. */
export const LoginSchema = z.object({
  email: EmailSchema,
  password: LoginPasswordSchema,
  turnstileToken: TurnstileSchema,
});
export type Login = z.infer<typeof LoginSchema>;

/* POST /api/auth/change-password — while signed in. */
export const ChangePasswordSchema = z.object({
  /** Required only if the account already has a password. */
  currentPassword: PasswordSchema.optional(),
  newPassword: PasswordSchema,
});
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  /** null until onboarding finishes. */
  handle: z.string().nullable(),
  onboarded: z.boolean(),
  /** Highest-privilege admin role, or null for normal users. */
  adminRole: z.enum(['superadmin', 'moderator', 'support']).nullable(),
  /** Set when the account is under an active sanction. */
  suspendedUntil: z.iso.datetime().nullable(),
  /** true once a password is set — drives the "Set / Change password" row. */
  hasPassword: z.boolean(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const VerifyOtpResponseSchema = z.object({
  token: z.string(),
  user: SessionUserSchema,
});
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;

/** Same shape as verify — a fresh token + the session user. */
export const LoginResponseSchema = VerifyOtpResponseSchema;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/* GET /api/auth/me */
export const MeResponseSchema = z.object({ user: SessionUserSchema });
export type MeResponse = z.infer<typeof MeResponseSchema>;
